import type { FC, KeyboardEventHandler } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import colors from 'app/styles/common/colors.module.scss';
import cn from 'classnames';
import type { Swiper as SwiperType } from 'swiper';
import { Mousewheel, Pagination, Scrollbar, Thumbs } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { isClient } from 'utils';
import { getTestIdByParts } from 'utils/getTestIdByParts';
import type { WithTestId } from 'types/app';

import { ImageFallback } from '@Atomic/molecules/ImageFallback';
import { Icons } from '@Atomic/atoms/Icons';
import { Image } from '@Atomic/atoms/Image';

import { swiperConfig, swiperThumbsConfig } from './config';
import styles from './style.module.scss';
import type { OfferCarouselProps } from './types';
import { getInitialSlide } from './utils';

export const OfferCarousel: FC<WithTestId<OfferCarouselProps>> = ({
  images,
  primaryId,
  isMobile,
  isExpired,
  overlay,
  testId,
  onClick,
  onSlideChange,
}) => {
  const [swiper, updateSwiper] = useState<SwiperType | null>(null);
  const swiperRef = useRef<SwiperType | null>(null);
  const [slideIndex, setIndex] = useState(0);

  const handledSlideChange = useCallback(
    (swiperState: SwiperType) => {
      if (swiperState) {
        setIndex(swiperState.activeIndex);

        if (onSlideChange) {
          onSlideChange(swiperState.activeIndex, swiperState.previousIndex);
        }
      }
    },
    [onSlideChange]
  );

  const paginationRef = useRef(null);

  const moreThanOne = images.length > 1;

  const initialSlide = useMemo(() => getInitialSlide(images, primaryId), [images, primaryId]);

  const handleWrapperKeyDown: KeyboardEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (e.code === 'Space' && onClick) {
        onClick();
      }
    },
    [onClick]
  );

  if (!isClient) {
    return null;
  }

  return (
    <div className={cn(styles.wrapper, { [styles.expired]: isExpired })} data-testid={testId}>
      <div className={styles.mainGalleryWrapper}>
        {overlay}
        {moreThanOne && !isMobile && (
          <div
            className={styles.galleryArrowsWrapper}
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={handleWrapperKeyDown}
          >
            <div className={styles.galleryArrowWrapper}>
              <button
                type="button"
                className={cn(styles.galleryArrow, styles.galleryArrowPrev)}
                onClick={(e) => {
                  e.stopPropagation();
                  swiperRef.current?.slidePrev();
                }}
                disabled={slideIndex === 0}
                data-testid={getTestIdByParts(testId, 'prev')}
              >
                <Icons.ChevronLeft color={colors.white} />
              </button>
            </div>
            <div className={styles.galleryArrowWrapper}>
              <button
                type="button"
                className={cn(styles.galleryArrow, styles.galleryArrowNext)}
                onClick={(e) => {
                  e.stopPropagation();
                  swiperRef.current?.slideNext();
                }}
                disabled={slideIndex === images.length - 1}
                data-testid={getTestIdByParts(testId, 'next')}
              >
                <Icons.ChevronRight color={colors.white} />
              </button>
            </div>
          </div>
        )}

        <Swiper
          modules={[Pagination, Thumbs]}
          {...swiperConfig}
          initialSlide={initialSlide}
          onSlideChange={handledSlideChange}
          className={cn(styles.mainGallery, { [styles.moreThanOne]: moreThanOne })}
          thumbs={{ swiper }}
          onBeforeInit={(swiper) => {
            swiperRef.current = swiper;
          }}
          pagination={{
            el: paginationRef.current,
            bulletClass: styles.bullet,
            bulletActiveClass: styles.bulletActive,
          }}
          data-testid={getTestIdByParts(testId, 'list')}
        >
          {images.map(({ alt, image, id }, index) => (
            <SwiperSlide
              key={id || index}
              className={styles.gallerySlide}
              data-testid={getTestIdByParts(testId, { value: 'item', index })}
              onClick={onClick}
            >
              <Image
                className={styles.galleryImage}
                fallback={<ImageFallback type="full" />}
                objectFit="contain"
                alt={alt}
                title={alt}
                src={image}
                blurredPad
                // Same photo the SSR placeholder painted: already in cache, no placeholder flash
                priority={index === initialSlide}
              />
            </SwiperSlide>
          ))}
        </Swiper>

        {moreThanOne && <div className={styles.bullets} ref={paginationRef} />}
      </div>

      <div className={cn(styles.thumbsGalleryWrapper, { [styles.moreThanOne]: moreThanOne })}>
        <Swiper
          modules={[Thumbs, Scrollbar, Mousewheel]}
          {...swiperThumbsConfig}
          className={styles.thumbsGallery}
          onSwiper={updateSwiper}
        >
          {images.map(({ alt, image, id }) => (
            <SwiperSlide key={id} className={styles.thumbsSlide}>
              <Image
                className={styles.thumbImage}
                fallback={<ImageFallback type="thumbnail" />}
                title={alt}
                src={image}
                alt={alt}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
};
